import React, { useEffect, useState } from "react";
import { X, Filter } from "lucide-react";
import styles from "./FilterPanel.module.scss";
import { useFilterStore } from "../../../../supplierEcommerce/store";

interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  options?: FilterOptions[];
}

export function FilterPanel({
  isOpen,
  onClose,
  options,
}: FilterPanelProps) {
  const applyArrayFilter = useFilterStore((state) => state.applyArrayFilter);
  const setPrice = useFilterStore((state) => state.setPrice);
  const clearFilters = useFilterStore((state) => state.clearFilters);
  const minPrice = useFilterStore((state) => state.minPrice);
  const maxPrice = useFilterStore((state) => state.maxPrice);
  const filter = useFilterStore((state) => state.filter);

  const colorOptions =
    options?.find((opt) => opt.id === "color")?.options || [];
  const brandOptions =
    options?.find((opt) => opt.id === "brand")?.options || [];
  const materialOptions =
    options?.find((opt) => opt.id === "material")?.options || [];
  const lineOptions = options?.find((opt) => opt.id === "line")?.options || [];

  const [priceMin, setPriceMin] = useState<string>("");
  const [priceMax, setPriceMax] = useState<string>("");

  useEffect(() => {
    if (filter?.price?.length === 2) {
      setPriceMin(filter.price[0].toString());
      setPriceMax(filter.price[1].toString());
    }
  }, [filter?.price]);

  if (!isOpen) return null;

  const handleToggle = (
    category: "color" | "brand" | "material" | "line",
    value: string
  ) => {
    applyArrayFilter(category, value);
  };

  const handlePriceChange = (min: string, max: string) => {
    setPriceMin(min);
    setPriceMax(max);
    if (min === "" || max === "") return;
    const minVal = Number(min);
    const maxVal = Number(max);
    if (!isNaN(minVal) && !isNaN(maxVal) && maxVal >= minVal) {
      setPrice([minVal, maxVal]);
    }
  };

  return (
    <aside className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Filter className={styles.headerIcon} />
            <h3 className={styles.title}>Filtros</h3>
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <X className={styles.closeIcon} />
          </button>
        </div>
        <p className={styles.subtitle}>Refina tu búsqueda de productos</p>
      </div>

      <div className={styles.content}>
        <div className={styles.filterSection}>
          <h4 className={styles.filterTitle}>Color</h4>
          <div className={styles.filterOptions}>
            {colorOptions.map((color) => (
              <div key={color.value} className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  id={`color-${color.value}`}
                  className={styles.checkbox}
                  onChange={() => handleToggle("color", color.value)}
                  checked={filter.color.includes(color.value)}
                />
                <label
                  htmlFor={`color-${color.value}`}
                  className={styles.label}
                >
                  {color.label ?? color.value}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.separator} />

        <div className={styles.filterSection}>
          <h4 className={styles.filterTitle}>Marca</h4>
          <div className={styles.filterOptions}>
            {brandOptions.map((brand) => (
              <div key={brand.value} className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  id={`brand-${brand.value}`}
                  className={styles.checkbox}
                  onChange={() => handleToggle("brand", brand.value)}
                  checked={filter.brand.includes(brand.value)}
                />
                <label
                  htmlFor={`brand-${brand.value}`}
                  className={styles.label}
                >
                  {brand.label ?? brand.value}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.separator} />

        <div className={styles.filterSection}>
          <h4 className={styles.filterTitle}>Material</h4>
          <div className={styles.filterOptions}>
            {materialOptions.map((material) => (
              <div key={material.value} className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  id={`material-${material.value}`}
                  className={styles.checkbox}
                  onChange={() => handleToggle("material", material.value)}
                  checked={filter.material.includes(material.value)}
                />
                <label
                  htmlFor={`material-${material.value}`}
                  className={styles.label}
                >
                  {material.label ?? material.value}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.separator} />

        <div className={styles.filterSection}>
          <h4 className={styles.filterTitle}>Línea</h4>
          <div className={styles.filterOptions}>
            {lineOptions.map((line) => (
              <div key={line.value} className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  id={`line-${line.value}`}
                  className={styles.checkbox}
                  onChange={() => handleToggle("line", line.value)}
                  checked={filter.line.includes(line.value)}
                />
                <label htmlFor={`line-${line.value}`} className={styles.label}>
                  {line.label ?? line.value}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.separator} />

        <div className={styles.filterSection}>
          <h4 className={styles.filterTitle}>Rango de Precio</h4>
          <div className={styles.rangeInputs}>
            <div className={styles.inputGroup}>
              <label htmlFor="price-min" className={styles.inputLabel}>
                Mínimo
              </label>
              <input
                type="number"
                id="price-min"
                placeholder="$0"
                className={styles.input}
                value={priceMin}
                onChange={(e) => handlePriceChange(e.target.value, priceMax)}
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="price-max" className={styles.inputLabel}>
                Máximo
              </label>
              <input
                type="number"
                id="price-max"
                placeholder="$1,000,000"
                className={styles.input}
                value={priceMax}
                onChange={(e) => handlePriceChange(priceMin, e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className={styles.separator} />
      </div>

      <div className={styles.footer}>
        <button className={styles.applyButton} onClick={() => onClose()}>
          Aplicar Filtros
        </button>
        <button
          className={styles.clearButton}
          onClick={() => {
            clearFilters();
            setPriceMin(minPrice.toString());
            setPriceMax(maxPrice.toString());
          }}
        >
          Limpiar Todo
        </button>
      </div>
    </aside>
  );
}
